import type { RejectReason } from "@developerz.ai/ggcoin-sdk";

/**
 * Map a server reject `reason` code (the closed `RejectReason` set, re-exported
 * by `@developerz.ai/ggcoin-sdk`) to a human-readable headline + explanation + a next step.
 * The raw codes (`wallet-frozen`, `velocity-exceeded`, …) are stable API
 * contract — never show them to users on their own. Typing the parameter as the
 * union makes the switch exhaustive: a new reason code fails the build here.
 */
export interface RejectCopy {
  title: string;
  detail: string;
  hint?: string;
}

export function describeRejectReason(reason: RejectReason): RejectCopy {
  switch (reason) {
    case "wallet-frozen":
      return {
        title: "Your wallet is frozen",
        detail: "Spends are blocked while your master wallet is frozen.",
        hint: "Unfreeze it on the Dashboard, then try again.",
      };
    case "sub-wallet-frozen":
      return {
        title: "This sub-wallet is frozen",
        detail: "The sub-wallet you're sending from is frozen.",
        hint: "Unfreeze it under Sub-wallets, or send from a different one.",
      };
    case "insufficient-balance":
      return {
        title: "Not enough balance",
        detail: "This sub-wallet doesn't hold enough coin for that amount.",
        hint: "Top it up under Sub-wallets, or send a smaller amount.",
      };
    case "over-spend-cap":
      return {
        title: "Over the spend cap",
        detail: "The amount is above this sub-wallet's per-spend cap.",
        hint: "Raise the cap under Sub-wallets, or send a smaller amount.",
      };
    case "velocity-exceeded":
      return {
        title: "Sending too fast",
        detail: "You've hit this sub-wallet's velocity limit — too much sent in a short window.",
        hint: "Wait a moment and try again, or send a smaller amount.",
      };
    case "sub-wallet-not-found":
    case "wallet-not-found":
      return {
        title: "Wallet not found",
        detail: "We couldn't find that wallet anymore.",
        hint: "Reload the page and try again.",
      };
    default: {
      // Exhaustiveness guard: adding a RejectReason without a case above fails
      // the build here. The runtime fallback still covers a server that sends a
      // code newer than this SDK build.
      const exhaustive: never = reason;
      return {
        title: "Transfer rejected",
        detail: `The transfer was rejected (${String(exhaustive)}).`,
      };
    }
  }
}
